import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Linkedin, Twitter, Facebook, Instagram, ArrowUp } from 'lucide-react';

const Footer = () => {
  const { t, language, changeLanguage } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const languages = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
    { code: 'hu', label: 'Magyar' },
  ];

  return (
    <footer className="relative bg-slate-950 border-t border-slate-800">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <a href="#" className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <span className="text-slate-900 font-bold text-xl">F</span>
              </div>
              <span className="text-2xl font-bold text-white tracking-tight">
                Flex<span className="text-amber-400">Co</span>
              </span>
            </a>
            <p className="text-slate-400 mb-6 max-w-md leading-relaxed">
              {t.footer.description}
            </p>
            {/* Social Links */}
            <div className="flex gap-4">
              {[Linkedin, Twitter, Facebook, Instagram].map((Icon, index) => (
                <a
                  key={index}
                  href="#"
                  className="w-10 h-10 rounded-lg bg-slate-800/50 flex items-center justify-center text-slate-400 hover:text-amber-400 hover:bg-slate-800 transition-all duration-300"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-4">
              {[
                { key: 'services', href: '#services' },
                { key: 'about', href: '#team' },
                { key: 'contact', href: '#contact' },
              ].map((link) => (
                <li key={link.key}>
                  <a
                    href={link.href}
                    className="text-slate-400 hover:text-amber-400 transition-colors duration-300"
                  >
                    {t.footer.links[link.key]}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Language & Legal */}
          <div>
            <h3 className="text-white font-semibold mb-6">Language</h3>
            <div className="space-y-2 mb-8">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`block text-sm transition-colors duration-300 ${
                    language === lang.code
                      ? 'text-amber-400'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href="#"
                  className="text-slate-400 hover:text-amber-400 transition-colors duration-300 text-sm"
                >
                  {t.footer.links.privacy}
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-400 hover:text-amber-400 transition-colors duration-300 text-sm"
                >
                  {t.footer.links.terms}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            {t.footer.copyright}
          </p>
          <button
            onClick={scrollToTop}
            className="group flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors duration-300"
          >
            <span className="text-sm">Back to top</span>
            <div className="w-8 h-8 rounded-lg bg-slate-800/50 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors duration-300">
              <ArrowUp className="w-4 h-4" />
            </div>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
